/**
 * İzlenebilirlik — Meta Pharma ERP (spec msg-194).
 *
 * ÜÇ GİRİŞ NOKTASI = TEK DETAIL:
 *   A) Üretim No yaz → Ara       → showDetail(prod_no)
 *   B) Tamamlanan Üretimler liste → Üretim No'ya tıkla → showDetail(prod_no)
 *   C) Koli etiketi QR (?prod_no=...&auto=1) → auto-search & showDetail
 *
 * Hepsi aynı backend endpoint (`/traceability/production/{prod_no}/bundle`)
 * ve aynı `<TraceabilityDetail>` component'ini kullanır.
 *
 * READ-ONLY. Fiyat / maliyet / kâr HİÇBİR YERDE GÖSTERİLMEZ (madde 21, 27, 46-47, 56).
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, Card, Btn, Badge } from "@/components/UI";
import {
  Search, Factory, Package, ShoppingCart, ShieldCheck, Truck, ClipboardList,
  ArrowLeft, FileText, Filter, ExternalLink, Beaker, History,
  Fingerprint, Database, PlayCircle, ChevronDown, ChevronRight, AlertTriangle, CheckCircle, Info,
} from "lucide-react";
import { API } from "@/lib/auth";
import axios from "axios";
import { openPdfPreview } from "@/lib/pdfPreview";

/* ---------- Helper ---------- */
const fmtDt = (s, dateOnly = false) => {
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return dateOnly ? d.toLocaleDateString("tr-TR") : d.toLocaleString("tr-TR");
};
const SHIP_LABEL = {
  SEVKIYAT_BEKLIYOR: { label: "Sevkiyat Bekliyor", level: "attention" },
  KISMI_SEVK: { label: "Kısmi Sevk", level: "attention" },
  SEVK_EDILDI: { label: "Sevk Edildi", level: "ok" },
  IPTAL: { label: "İptal", level: "missing" },
  TESLIM_EDILDI: { label: "Teslim Edildi", level: "ok" },
};

/* ============================================================
   MAIN PAGE
   ============================================================ */
/* ============================================================
   TOP-LEVEL — 3 SEKMELİ İZLENEBİLİRLİK MERKEZİ (Prompt 1, iter75)
   ============================================================ */
export default function TraceabilityCenter() {
  const [sp, setSp] = useSearchParams();
  const initialTab = sp.get("tab") || "production";
  const [tab, setTab] = useState(initialTab);
  // ?prod_no=...&auto=1 varsa production tab'ında kal
  useEffect(() => {
    if (sp.get("prod_no")) setTab("production");
  }, [sp]);

  const TABS = [
    { key: "production", label: "Üretim İzlenebilirliği", icon: Factory },
    { key: "audit", label: "Sistem İzlenebilirliği", icon: Fingerprint },
    { key: "consistency", label: "Veri İzlenebilirliği", icon: Database },
  ];

  return (
    <div>
      <PageHeader title="İzlenebilirlik"
                  description="Üretim geçmişi · Sistem denetim kayıtları · Veri tutarlılığı"/>
      <div className="mb-4 flex flex-wrap items-center gap-1 border-b border-slate-200">
        {TABS.map(t => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key}
                    onClick={() => { setTab(t.key); setSp(new URLSearchParams({ tab: t.key })); }}
                    data-testid={`trace-tab-${t.key}`}
                    className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition -mb-px ${active ? "border-indigo-600 text-indigo-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              <Icon size={14}/> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "production" && <ProductionTraceabilityTab />}
      {tab === "audit" && <SystemAuditTab />}
      {tab === "consistency" && <DataConsistencyTab />}
    </div>
  );
}

/* ============================================================
   TAB 1 — ÜRETİM İZLENEBİLİRLİĞİ (mevcut logic, aynen korundu)
   ============================================================ */
function ProductionTraceabilityTab() {
  const [prodNo, setProdNo] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [bundle, setBundle] = useState(null);
  const [sp] = useSearchParams();
  const nav = useNavigate();

  const runSearch = useCallback(async (value) => {
    const v = (value || "").trim();
    if (!v) return;
    setBusy(true); setError(null); setBundle(null);
    try {
      const r = await api.get(`/traceability/production/${encodeURIComponent(v)}/bundle`);
      setBundle(r);
    } catch (err) {
      setError(err.response?.data?.detail || "Üretim bulunamadı");
    } finally { setBusy(false); }
  }, []);

  // QR ile ?prod_no=...&auto=1 → auto search
  useEffect(() => {
    const qp = sp.get("prod_no");
    const auto = sp.get("auto");
    if (qp && auto === "1") { setProdNo(qp); runSearch(qp); }
    else if (qp && !prodNo) { setProdNo(qp); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp]);

  const search = (e) => { if (e) e.preventDefault(); runSearch(prodNo); };
  const clearDetail = () => { setBundle(null); setError(null); };

  return (
    <div>
      {!bundle && (
        <>
          {/* Arama */}
          <Card className="p-4 mb-4">
            <form onSubmit={search} className="flex flex-wrap gap-2 items-end">
              <div className="flex-1 min-w-[280px]">
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold pl-0.5">Üretim Numarası</label>
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-3 text-slate-400"/>
                  <input value={prodNo} onChange={e => setProdNo(e.target.value)}
                         placeholder="Örn: UE-10542, MP2608272..."
                         className="w-full pl-9 pr-3 py-2.5 text-sm border border-slate-300 rounded-lg focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none"
                         data-testid="trace-search-input" autoFocus/>
                </div>
              </div>
              <Btn type="submit" disabled={busy || !prodNo.trim()} data-testid="trace-search-submit">
                <Search size={14}/> {busy ? "Aranıyor..." : "Ara"}
              </Btn>
            </form>
            {error && <div className="mt-3 bg-rose-50 border border-rose-200 rounded p-2 text-xs text-rose-800" data-testid="trace-error">{error}</div>}
          </Card>

          {/* Tamamlanan Üretimler */}
          <CompletedProductionsList onOpen={(pn) => runSearch(pn)} />
        </>
      )}

      {bundle && (
        <TraceabilityDetail
          bundle={bundle}
          onBack={clearDetail}
          nav={nav}
        />
      )}
    </div>
  );
}

/* ============================================================
   COMPLETED PRODUCTIONS LIST — Tamamlanan Üretimler
   ============================================================ */
function CompletedProductionsList({ onOpen }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [f, setF] = useState({ q: "", customer_id: "", product_id: "", project_no: "", shipment_status: "", date_from: "", date_to: "" });

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = {};
      Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await api.get("/traceability/completed-productions", params);
      setRows(r);
    } finally { setBusy(false); }
  }, [f]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    (async () => {
      const [c, p] = await Promise.all([api.get("/customers"), api.get("/products")]);
      setCustomers(c); setProducts(p);
    })();
  }, []);

  const pickName = (o) => o?.legal_name || o?.name || o?.company_name || "—";

  const applyPreset = (preset) => {
    const now = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (preset === "today") setF(x => ({ ...x, date_from: iso(now), date_to: iso(now) }));
    else if (preset === "week") {
      const d = new Date(now); d.setDate(d.getDate() - 7);
      setF(x => ({ ...x, date_from: iso(d), date_to: iso(now) }));
    } else if (preset === "month") {
      const d = new Date(now.getFullYear(), now.getMonth(), 1);
      setF(x => ({ ...x, date_from: iso(d), date_to: iso(now) }));
    } else if (preset === "year") {
      const d = new Date(now.getFullYear(), 0, 1);
      setF(x => ({ ...x, date_from: iso(d), date_to: iso(now) }));
    } else if (preset === "clear") {
      setF({ q: "", customer_id: "", product_id: "", project_no: "", shipment_status: "", date_from: "", date_to: "" });
    }
  };

  const downloadListPdf = async () => {
    const params = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v) params.set(k, v); });
    const r = await axios.get(`${API}/traceability/completed-productions/pdf?${params.toString()}`, { responseType: "blob" });
    openPdfPreview(r.data, "izlenebilirlik_listesi.pdf");
  };

  return (
    <Card className="p-4" data-testid="completed-productions-list">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-700 flex items-center gap-2">
          <Factory size={14}/> Tamamlanan Üretimler
          <span className="text-[10px] font-mono bg-slate-100 px-2 py-0.5 rounded-full text-slate-600">{rows.length}</span>
        </h3>
        <div className="flex gap-1">
          <Btn size="sm" variant="secondary" onClick={downloadListPdf} data-testid="trace-list-pdf" title="Tamamlanan Üretimler Listesi PDF (Landscape)">
            <FileText size={12}/>
          </Btn>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3 text-xs">
        <input placeholder="Üretim No..." value={f.q} onChange={e => setF({...f, q: e.target.value})}
               className="px-2 py-1.5 border rounded" data-testid="trace-filter-q"/>
        <select value={f.customer_id} onChange={e => setF({...f, customer_id: e.target.value})}
                className="px-2 py-1.5 border rounded" data-testid="trace-filter-customer">
          <option value="">Tüm Müşteriler</option>
          {customers.map(c => <option key={c.id} value={c.id}>{pickName(c)}</option>)}
        </select>
        <select value={f.product_id} onChange={e => setF({...f, product_id: e.target.value})}
                className="px-2 py-1.5 border rounded" data-testid="trace-filter-product">
          <option value="">Tüm Ürünler</option>
          {products.map(p => <option key={p.id} value={p.id}>{pickName(p)}</option>)}
        </select>
        <input placeholder="Proje No..." value={f.project_no} onChange={e => setF({...f, project_no: e.target.value})}
               className="px-2 py-1.5 border rounded" data-testid="trace-filter-project"/>
        <select value={f.shipment_status} onChange={e => setF({...f, shipment_status: e.target.value})}
                className="px-2 py-1.5 border rounded" data-testid="trace-filter-shipment">
          <option value="">Tüm Sevkiyat Durumları</option>
          <option value="SEVKIYAT_BEKLIYOR">Sevkiyat Bekliyor</option>
          <option value="KISMI_SEVK">Kısmi Sevk</option>
          <option value="SEVK_EDILDI">Sevk Edildi</option>
        </select>
        <input type="date" value={f.date_from} onChange={e => setF({...f, date_from: e.target.value})}
               className="px-2 py-1.5 border rounded" data-testid="trace-filter-date-from"/>
        <input type="date" value={f.date_to} onChange={e => setF({...f, date_to: e.target.value})}
               className="px-2 py-1.5 border rounded" data-testid="trace-filter-date-to"/>
        <div className="flex gap-1">
          <button onClick={() => applyPreset("today")} className="px-2 py-1 text-[10px] bg-slate-100 rounded hover:bg-slate-200">Bugün</button>
          <button onClick={() => applyPreset("week")} className="px-2 py-1 text-[10px] bg-slate-100 rounded hover:bg-slate-200">Hafta</button>
          <button onClick={() => applyPreset("month")} className="px-2 py-1 text-[10px] bg-slate-100 rounded hover:bg-slate-200">Ay</button>
          <button onClick={() => applyPreset("year")} className="px-2 py-1 text-[10px] bg-slate-100 rounded hover:bg-slate-200">Yıl</button>
          <button onClick={() => applyPreset("clear")} className="px-2 py-1 text-[10px] bg-red-50 text-red-700 rounded hover:bg-red-100">Sıfırla</button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50">
            <tr>
              <th className="px-2 py-1.5 text-left">Üretim No</th>
              <th className="px-2 py-1.5 text-left">Proje</th>
              <th className="px-2 py-1.5 text-left">Müşteri</th>
              <th className="px-2 py-1.5 text-left">Ürün</th>
              <th className="px-2 py-1.5 text-right">Sipariş</th>
              <th className="px-2 py-1.5 text-left">Tamamlandı</th>
              <th className="px-2 py-1.5 text-left">Sevkiyat</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-t hover:bg-indigo-50/50" data-testid={`trace-row-${r.prod_no}`}>
                <td className="px-2 py-1.5">
                  <button onClick={() => onOpen(r.prod_no)}
                          className="font-mono text-indigo-700 hover:underline font-semibold"
                          data-testid={`trace-open-${r.prod_no}`}>
                    {r.prod_no}
                  </button>
                </td>
                <td className="px-2 py-1.5 text-xs">{r.project_no || "—"}</td>
                <td className="px-2 py-1.5">{r.customer_name || "—"}</td>
                <td className="px-2 py-1.5">{r.product_name || "—"}</td>
                <td className="px-2 py-1.5 text-right font-mono">{r.order_qty || "—"}</td>
                <td className="px-2 py-1.5 text-xs">{fmtDt(r.completed_at, true)}</td>
                <td className="px-2 py-1.5">
                  <Badge level={SHIP_LABEL[r.shipment_status]?.level || "inactive"}>
                    {SHIP_LABEL[r.shipment_status]?.label || r.shipment_status || "—"}
                  </Badge>
                </td>
              </tr>
            ))}
            {rows.length === 0 && !busy && (
              <tr><td colSpan={7} className="px-2 py-6 text-center text-slate-400 text-sm">Kayıt yok</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {busy && <div className="text-xs text-slate-500 mt-2">Yükleniyor...</div>}
    </Card>
  );
}

/* ============================================================
   TRACEABILITY DETAIL — Üç giriş noktasının ortak component'i
   ============================================================ */
function TraceabilityDetail({ bundle, onBack, nav }) {
  const s = bundle.summary || {};
  const po = bundle.production_order || {};
  const so = bundle.sales_order;
  const fl = bundle.finished_lot;

  const downloadDetailPdf = async () => {
    const r = await axios.get(`${API}/traceability/production/${encodeURIComponent(s.prod_no)}/pdf`, { responseType: "blob" });
    openPdfPreview(r.data, `IZLENEBILIRLIK_${s.prod_no}.pdf`);
  };

  return (
    <div className="space-y-3" data-testid="trace-detail">
      <div className="flex items-center justify-between">
        <Btn variant="secondary" onClick={onBack} data-testid="trace-back"><ArrowLeft size={14}/> Geri</Btn>
        <Btn onClick={downloadDetailPdf} data-testid="trace-detail-pdf" title="İzlenebilirlik PDF İndir (Portrait)">
          <FileText size={14}/>
        </Btn>
      </div>

      {/* SUMMARY (madde 17-18) */}
      <Card className="p-4 bg-gradient-to-r from-indigo-50 to-violet-50 border-indigo-200">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase text-indigo-700 font-bold tracking-wider">Üretim Özeti</div>
            <div className="text-3xl font-mono font-bold text-slate-900" data-testid="trace-detail-prod-no">{s.prod_no}</div>
          </div>
          <Badge level={SHIP_LABEL[s.status]?.level || "attention"}>
            {SHIP_LABEL[s.status]?.label || s.status}
          </Badge>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <Kv label="Müşteri" value={s.customer_name}/>
          <Kv label="Ürün" value={s.product_name}/>
          <Kv label="Proje No" value={s.project_no}/>
          <Kv label="Sipariş Adedi" value={s.order_qty}/>
          <Kv label="Toplam Dolum" value={s.total_filled}/>
          <Kv label="Toplam Teslim" value={s.total_delivered}/>
          <Kv label="Kalan Stok" value={s.remaining_stock}/>
          <Kv label="Sevkiyat Durumu" value={SHIP_LABEL[s.status]?.label || s.status}/>
        </div>
      </Card>

      {/* 1. SALES ORDER */}
      {so && (
        <Section icon={<ShoppingCart size={14}/>} title="1. Satış Siparişi"
                 link={so.id && { to: `/sales-orders/${so.id}`, label: "Sipariş Detayı" }}>
          <KvGrid rows={[
            ["Sipariş No", so.order_no],
            ["Sipariş Tarihi", fmtDt(so.order_date || so.created_at)],
            ["Marka", so.brand_id || "—"],
            ["Öncelik", so.priority],
            ["Durum", so.status],
            ["Onay Tarihi", fmtDt(so.approved_at)],
            ["Oluşturan", so.created_by_name],
            ["Onaylayan", so.approved_by_name],
          ]}/>
        </Section>
      )}

      {/* 2. PRODUCTION */}
      <Section icon={<Factory size={14}/>} title="2. Üretim Bilgileri"
               link={{ to: `/production-orders/${po.id}`, label: "Üretim Detayı" }}>
        <KvGrid rows={[
          ["Üretim Tarihi", fmtDt(po.created_at)],
          ["Planlanan", po.planned_units],
          ["Üretilen (Filled)", s.total_filled],
          ["Actual Bulk (kg)", po.actual_bulk_kg],
          ["Planlanan Bulk (kg)", po.planned_batch_kg],
          ["Fire (kg)", po.waste_kg || 0],
          ["Durum", po.status],
          ["Tamamlanma", fmtDt(po.manually_completed_at || po.completed_at)],
          ["Üretimi Yapan", po.created_by_name],
          ["Tamamlayan", po.manually_completed_by_name],
          ["Notlar", po.notes || "—"],
        ]}/>
      </Section>

      {/* APPROVALS — spec msg-195 madde 27 */}
      {bundle.approvals && (
        <Section icon={<ShieldCheck size={14}/>} title="Onaylar">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { key: "production", title: "Üretimi Onaylayan" },
              { key: "qc", title: "QC Onaylayan" },
              { key: "final", title: "Son Onay" },
            ].map(({ key, title }) => {
              const a = bundle.approvals[key];
              const done = a?.status === "approved" && a?.at;
              return (
                <div key={key} className={`p-3 rounded border ${done ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`} data-testid={`trace-approval-${key}`}>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-600 mb-1">{title}</div>
                  {done ? (
                    <>
                      <div className="text-sm font-semibold text-slate-900">{a.user_name || "—"}</div>
                      <div className="text-xs text-slate-600 font-mono">{fmtDt(a.at)}</div>
                    </>
                  ) : (
                    <div className="text-xs text-amber-700 font-semibold italic">Onay Bekliyor</div>
                  )}
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {/* 3. WEIGHING / LOTS */}
      {(bundle.materials_used || []).length > 0 && (
        <Section icon={<Beaker size={14}/>} title="3. Tartım / Kullanılan Lotlar">
          <SmallTable
            columns={["Kod", "Malzeme", "Gerekli", "Kullanılan", "Birim", "Lot No", "Tedarikçi", "SKT"]}
            rows={bundle.materials_used.map(m => [
              m.material_code, m.material_name, m.required_qty ?? "—", m.actual_qty ?? "—",
              m.unit, m.lot_code, m.supplier || "—", fmtDt(m.expiry_date, true) || "—",
            ])}
            testid="trace-materials"
          />
        </Section>
      )}

      {/* 4. PACKAGING */}
      {(bundle.packaging_used || []).length > 0 && (
        <Section icon={<Package size={14}/>} title="4. Ambalaj">
          <SmallTable
            columns={["Kod", "Ambalaj", "Adet/Birim", "Birim", "Kaynak"]}
            rows={bundle.packaging_used.map(p => [
              p.material_code || "—", p.material_name || "—",
              p.qty_per_unit ?? "—", p.unit,
              p.is_manual ? "Manuel" : "Envanter",
            ])}
            testid="trace-packaging"
          />
        </Section>
      )}

      {/* 5. QC */}
      {(bundle.qc_records || []).length > 0 && (
        <Section icon={<ShieldCheck size={14}/>} title="5. Kalite Kontrol">
          {bundle.qc_records.map((qc, i) => (
            <div key={i} className="mb-3 last:mb-0 border-l-4 border-indigo-300 pl-3">
              <div className="text-xs text-slate-600 mb-1">
                <span className="font-semibold">{(qc.kind || "").toUpperCase()}</span> · {fmtDt(qc.created_at)} ·{" "}
                <Badge level={qc.result === "pass" ? "ok" : qc.result === "fail" ? "missing" : "attention"}>{qc.result || "—"}</Badge>{" "}
                · {qc.tested_by_name}
              </div>
              {(qc.specs || []).length > 0 && (
                <SmallTable
                  columns={["Parametre", "Limit", "Ölçülen", "Birim"]}
                  rows={qc.specs.map(sp => [
                    sp.name, `${sp.lower ?? "—"} — ${sp.upper ?? "—"}`,
                    sp.measured ?? "—", sp.unit,
                  ])}
                />
              )}
            </div>
          ))}
        </Section>
      )}

      {/* 6. FILLINGS */}
      {(bundle.filling_operations || []).length > 0 && (
        <Section icon={<Factory size={14}/>} title="6. Dolumlar">
          <SmallTable
            columns={["Dolum", "Tarih", "Miktar", "Kullanılan Bulk (kg)", "İşlemi Yapan"]}
            rows={bundle.filling_operations.map((f, i) => [
              `D-${String(i + 1).padStart(2, "0")}`,
              fmtDt(f.timestamp || f.date),
              f.accepted_units ?? f.qty ?? 0,
              f.bulk_used_kg ?? "—",
              f.user_name || "—",
            ])}
            testid="trace-fillings"
          />
        </Section>
      )}

      {/* 7. FINISHED PRODUCT */}
      {fl && (
        <Section icon={<Package size={14}/>} title="7. Bitmiş Ürün">
          <KvGrid rows={[
            ["Lot Kodu", fl.lot_code],
            ["Toplam Dolum", fl.qty ?? fl.total_filled ?? 0],
            ["Teslim Edilen", fl.delivered ?? s.total_delivered],
            ["Kalan", s.remaining_stock],
          ]}/>
        </Section>
      )}

      {/* 8. DELIVERIES */}
      {(bundle.deliveries || []).length > 0 && (
        <Section icon={<Truck size={14}/>} title="8. Teslimatlar">
          <SmallTable
            columns={["Tarih", "İrsaliye No", "Teslim", "Önceki", "Kalan", "Teslim Alan", "Taşıyıcı", "Durum"]}
            rows={bundle.deliveries.map(d => [
              fmtDt(d.date, true),
              d.shipment_id
                ? <Link key="l" to={`/finished-products`} className="text-indigo-600 hover:underline font-mono">{d.irsaliye_no}</Link>
                : (d.irsaliye_no || "—"),
              d.qty ?? 0,
              d.previous_delivered ?? 0,
              d.remaining ?? "—",
              d.recipient || "—",
              d.carrier || "—",
              <Badge key="s" level={SHIP_LABEL[d.status]?.level || "attention"}>{SHIP_LABEL[d.status]?.label || d.status}</Badge>,
            ])}
            testid="trace-deliveries"
          />
        </Section>
      )}

      {/* 9. TIMELINE */}
      {(bundle.timeline || []).length > 0 && (
        <Section icon={<History size={14}/>} title="9. İşlem Geçmişi">
          <ul className="text-xs space-y-1.5 pl-2" data-testid="trace-timeline">
            {bundle.timeline.map((t, i) => (
              <li key={i} className="flex gap-3 border-l-2 border-slate-200 pl-3">
                <span className="text-slate-500 font-mono w-40 flex-shrink-0">{fmtDt(t.at)}</span>
                <span className="text-slate-800 flex-1">{t.event}</span>
                {t.user && <span className="text-slate-400">{t.user}</span>}
              </li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}

/* ---------- UI helpers ---------- */
function Kv({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase font-semibold text-slate-500 mb-0.5">{label}</div>
      <div className="text-sm font-semibold text-slate-800">{value ?? "—"}</div>
    </div>
  );
}
function KvGrid({ rows }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
      {rows.map(([k, v], i) => <Kv key={i} label={k} value={v}/>)}
    </div>
  );
}
function Section({ icon, title, link, children }) {
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-700 flex items-center gap-2">
          {icon}{title}
        </h4>
        {link && (
          <Link to={link.to} className="text-[11px] text-indigo-600 hover:underline flex items-center gap-1">
            {link.label} <ExternalLink size={11}/>
          </Link>
        )}
      </div>
      {children}
    </Card>
  );
}
function SmallTable({ columns, rows, testid }) {
  return (
    <div className="overflow-x-auto" data-testid={testid}>
      <table className="min-w-full text-xs">
        <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50">
          <tr>{columns.map((c, i) => <th key={i} className="px-2 py-1.5 text-left">{c}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t">
              {r.map((c, j) => <td key={j} className="px-2 py-1.5">{c ?? "—"}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ============================================================
   TAB 2 — SİSTEM İZLENEBİLİRLİĞİ (Central Audit) — iter75 Prompt 1
   ============================================================ */
function SystemAuditTab() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [opts, setOpts] = useState({ entities: [], actions: [], users: [] });
  const [f, setF] = useState({ start: "", end: "", user_id: "", entity: "", action: "", q: "" });
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = {};
      Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await api.get("/audit/events", { ...params, limit: 300 });
      setRows(r);
    } finally { setBusy(false); }
  }, [f]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/audit/filter-options").then(setOpts).catch(() => {}); }, []);

  const openDetail = async (row) => {
    try {
      const r = await api.get(`/audit/events/${encodeURIComponent(row.timestamp)}`);
      setDetail(r);
    } catch (err) { setDetail({ ...row, diff: [], error: "Detay yüklenemedi" }); }
  };

  return (
    <div>
      <Card className="p-3 mb-3">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-xs">
          <div><label className="text-[10px] uppercase text-slate-500 block mb-0.5">Başlangıç</label>
            <input type="date" value={f.start.slice(0, 10)} onChange={e => setF({ ...f, start: e.target.value })} data-testid="audit-f-start" className="w-full border rounded px-2 py-1.5"/></div>
          <div><label className="text-[10px] uppercase text-slate-500 block mb-0.5">Bitiş</label>
            <input type="date" value={f.end.slice(0, 10)} onChange={e => setF({ ...f, end: e.target.value })} data-testid="audit-f-end" className="w-full border rounded px-2 py-1.5"/></div>
          <div><label className="text-[10px] uppercase text-slate-500 block mb-0.5">Kullanıcı</label>
            <select value={f.user_id} onChange={e => setF({ ...f, user_id: e.target.value })} data-testid="audit-f-user" className="w-full border rounded px-2 py-1.5">
              <option value="">Tümü</option>
              {opts.users.map(u => <option key={u} value={u}>{u}</option>)}
            </select></div>
          <div><label className="text-[10px] uppercase text-slate-500 block mb-0.5">Modül</label>
            <select value={f.entity} onChange={e => setF({ ...f, entity: e.target.value })} data-testid="audit-f-entity" className="w-full border rounded px-2 py-1.5">
              <option value="">Tümü</option>
              {opts.entities.map(e => <option key={e} value={e}>{e}</option>)}
            </select></div>
          <div><label className="text-[10px] uppercase text-slate-500 block mb-0.5">İşlem</label>
            <select value={f.action} onChange={e => setF({ ...f, action: e.target.value })} data-testid="audit-f-action" className="w-full border rounded px-2 py-1.5">
              <option value="">Tümü</option>
              {opts.actions.map(a => <option key={a} value={a}>{a}</option>)}
            </select></div>
          <div><label className="text-[10px] uppercase text-slate-500 block mb-0.5">Arama</label>
            <input value={f.q} onChange={e => setF({ ...f, q: e.target.value })} placeholder="Kullanıcı / Kayıt No"
                   data-testid="audit-f-q" className="w-full border rounded px-2 py-1.5"/></div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="px-3 py-2 border-b bg-slate-50 flex items-center justify-between">
          <div className="text-xs font-semibold text-slate-700">
            {busy ? "Yükleniyor…" : `${rows.length} kayıt`}
            <span className="ml-3 text-[10px] font-normal text-slate-500 italic">Audit read-only · Düzenleme yok</span>
          </div>
          {/* iter245 — Export (CSV + PDF). Filters mirror the current audit view. */}
          <div className="flex gap-1">
            <button
              onClick={() => {
                const params = new URLSearchParams({ format: "csv", limit: "5000" });
                Object.entries(f).forEach(([k, v]) => { if (v) params.set(k, v); });
                const token = localStorage.getItem("erp_token");
                fetch(`${process.env.REACT_APP_BACKEND_URL}/api/audit/events/export?${params}`,
                      { headers: { Authorization: `Bearer ${token}` } })
                  .then(r => r.blob())
                  .then(b => { const u = URL.createObjectURL(b); const a = document.createElement("a"); a.href = u; a.download = `audit_${Date.now()}.csv`; a.click(); });
              }}
              data-testid="audit-export-csv"
              className="text-[11px] px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 border">CSV</button>
            <button
              onClick={() => {
                const params = new URLSearchParams({ format: "pdf", limit: "2000" });
                Object.entries(f).forEach(([k, v]) => { if (v) params.set(k, v); });
                const token = localStorage.getItem("erp_token");
                fetch(`${process.env.REACT_APP_BACKEND_URL}/api/audit/events/export?${params}`,
                      { headers: { Authorization: `Bearer ${token}` } })
                  .then(r => r.blob())
                  .then(b => { const u = URL.createObjectURL(b); const a = document.createElement("a"); a.href = u; a.download = `audit_${Date.now()}.pdf`; a.click(); });
              }}
              data-testid="audit-export-pdf"
              className="text-[11px] px-2 py-1 rounded bg-indigo-600 hover:bg-indigo-700 text-white">PDF</button>
          </div>
        </div>
        <div className="overflow-x-auto max-h-[600px]" data-testid="audit-table">
          <table className="min-w-full text-xs">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50 sticky top-0">
              <tr>
                {["Tarih/Saat", "Kullanıcı", "Modül", "Kayıt", "İşlem", "Kısa Açıklama", ""].map((h, i) => (
                  <th key={i} className="px-2 py-1.5 text-left">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t hover:bg-indigo-50/40" data-testid={`audit-row-${i}`}>
                  <td className="px-2 py-1.5 whitespace-nowrap font-mono text-[10px]">{new Date(r.timestamp).toLocaleString("tr-TR")}</td>
                  <td className="px-2 py-1.5">{r.user_email || "—"}</td>
                  <td className="px-2 py-1.5"><Badge>{r.entity_label}</Badge></td>
                  <td className="px-2 py-1.5 font-mono text-[10px]">{(r.entity_id || "").slice(0, 12)}</td>
                  <td className="px-2 py-1.5 font-semibold">{r.action_label}</td>
                  <td className="px-2 py-1.5 text-slate-600">{r.summary}</td>
                  <td className="px-2 py-1.5"><button onClick={() => openDetail(r)} data-testid={`audit-open-${i}`} className="text-indigo-600 hover:underline">Detay</button></td>
                </tr>
              ))}
              {!rows.length && !busy && (
                <tr><td colSpan="7" className="p-6 text-center text-slate-400">Kayıt yok</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {detail && <AuditDetailDrawer detail={detail} onClose={() => setDetail(null)}/>}
    </div>
  );
}

function AuditDetailDrawer({ detail, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="audit-detail-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}/>
      <div className="relative bg-white w-full max-w-xl h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b p-3 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase text-slate-500">Audit Kaydı</div>
            <div className="text-sm font-semibold">{detail.action_label} · {detail.entity_label}</div>
          </div>
          <button onClick={onClose} data-testid="audit-close" className="text-slate-500 hover:text-slate-800">✕</button>
        </div>
        <div className="p-4 space-y-3 text-xs">
          <div className="grid grid-cols-2 gap-2">
            <div><span className="text-slate-500 uppercase text-[10px] block">Tarih</span><span className="font-mono">{new Date(detail.timestamp).toLocaleString("tr-TR")}</span></div>
            <div><span className="text-slate-500 uppercase text-[10px] block">Kullanıcı</span>{detail.user_email || "—"}</div>
            <div><span className="text-slate-500 uppercase text-[10px] block">Modül</span>{detail.entity_label}</div>
            <div><span className="text-slate-500 uppercase text-[10px] block">Kayıt ID</span><span className="font-mono">{detail.entity_id}</span></div>
            {detail.reason && <div className="col-span-2"><span className="text-slate-500 uppercase text-[10px] block">Neden</span>{detail.reason}</div>}
          </div>
          <div>
            <div className="text-xs font-semibold uppercase text-slate-700 mb-2 mt-3">Değişiklikler</div>
            {!(detail.diff || []).length && <div className="text-slate-400 italic">Alan bazlı değişiklik yok.</div>}
            <div className="space-y-1">
              {(detail.diff || []).map((d, i) => (
                <div key={i} className="grid grid-cols-[130px_1fr] gap-2 items-start bg-slate-50 rounded p-2" data-testid={`audit-diff-${i}`}>
                  <div className="font-mono text-[10px] uppercase text-slate-500">{d.field}</div>
                  <div className="text-xs">
                    <span className="line-through text-rose-600">{d.old}</span>
                    {" → "}
                    <span className="font-semibold text-emerald-700">{d.new}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   TAB 3 — VERİ İZLENEBİLİRLİĞİ (Consistency Center) — iter75 Prompt 1
   ============================================================ */
function DataConsistencyTab() {
  const [snap, setSnap] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expandedIdx, setExpandedIdx] = useState(-1);

  const loadLast = useCallback(async () => {
    try { const r = await api.get("/consistency/last"); setSnap(r); } catch {}
  }, []);

  useEffect(() => { loadLast(); }, [loadLast]);

  const runNow = async () => {
    setBusy(true);
    try {
      const r = await api.get("/consistency/check");
      setSnap(r);
    } catch (e) {
      alert("Kontrol başarısız: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const sevMeta = {
    critical: { label: "Kritik", cls: "text-rose-700 bg-rose-50 border-rose-200", icon: AlertTriangle },
    warning: { label: "Uyarı", cls: "text-amber-700 bg-amber-50 border-amber-200", icon: AlertTriangle },
    info: { label: "Bilgi", cls: "text-sky-700 bg-sky-50 border-sky-200", icon: Info },
  };

  return (
    <div>
      {/* KART ÖZETİ */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Card className="p-3 border-l-4 border-rose-500" data-testid="consist-card-critical">
          <div className="text-[10px] uppercase text-slate-500 font-semibold">Kritik Sorun</div>
          <div className="text-3xl font-bold text-rose-700 mt-1">{snap?.critical ?? 0}</div>
        </Card>
        <Card className="p-3 border-l-4 border-amber-500" data-testid="consist-card-warning">
          <div className="text-[10px] uppercase text-slate-500 font-semibold">Uyarı</div>
          <div className="text-3xl font-bold text-amber-700 mt-1">{snap?.warning ?? 0}</div>
        </Card>
        <Card className="p-3 border-l-4 border-emerald-500" data-testid="consist-card-rules">
          <div className="text-[10px] uppercase text-slate-500 font-semibold">Kontrol Edildi</div>
          <div className="text-3xl font-bold text-emerald-700 mt-1">{snap?.checked_rules ?? 0} <span className="text-sm text-slate-500 font-normal">kural</span></div>
        </Card>
        <Card className="p-3 border-l-4 border-slate-400" data-testid="consist-card-last">
          <div className="text-[10px] uppercase text-slate-500 font-semibold">Son Kontrol</div>
          <div className="text-sm font-semibold text-slate-700 mt-1">
            {snap?.run_at ? new Date(snap.run_at).toLocaleString("tr-TR") : "—"}
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">{snap?.run_by || ""}</div>
        </Card>
      </div>

      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-slate-500 italic">Bu kontroller salt-okuyucudur; hiçbir kayıt değiştirilmez. Otomatik: her gece 04:00 TR.</div>
        <Btn onClick={runNow} disabled={busy} data-testid="consist-run">
          <PlayCircle size={14}/> {busy ? "Kontrol Ediliyor…" : "Verileri Şimdi Kontrol Et"}
        </Btn>
      </div>

      {(snap?.errors || []).length > 0 && (
        <Card className="p-3 mb-3 border-l-4 border-orange-500 bg-orange-50" data-testid="consist-errors">
          <div className="text-xs font-semibold text-orange-800 mb-1">Kontrol motoru hataları</div>
          <ul className="text-[11px] text-orange-700 list-disc pl-4">
            {snap.errors.map((e, i) => <li key={i}><b>{e.rule}:</b> {e.error}</li>)}
          </ul>
        </Card>
      )}

      <Card className="overflow-hidden">
        <div className="px-3 py-2 border-b bg-slate-50 text-xs font-semibold text-slate-700">
          {(snap?.issues || []).length} bulgu
        </div>
        <div className="overflow-x-auto max-h-[500px]">
          <table className="min-w-full text-xs">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50 sticky top-0">
              <tr>
                {["Önem", "Modül", "Kayıt", "Sorun", "Fark", "Detay"].map((h, i) => (
                  <th key={i} className="px-2 py-1.5 text-left">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(snap?.issues || []).map((it, i) => {
                const meta = sevMeta[it.severity] || sevMeta.info;
                const Icon = meta.icon;
                return (
                  <tr key={i} className="border-t hover:bg-slate-50" data-testid={`consist-row-${i}`}>
                    <td className="px-2 py-1.5"><span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-semibold ${meta.cls}`}><Icon size={10}/> {meta.label}</span></td>
                    <td className="px-2 py-1.5"><Badge>{it.module}</Badge></td>
                    <td className="px-2 py-1.5 font-mono text-[10px]">{it.record}</td>
                    <td className="px-2 py-1.5">{it.problem}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px] text-slate-600">{it.diff || "—"}</td>
                    <td className="px-2 py-1.5">
                      {it.link && <Link to={it.link} className="text-indigo-600 hover:underline">Git</Link>}
                    </td>
                  </tr>
                );
              })}
              {!(snap?.issues || []).length && !busy && (
                <tr><td colSpan="6" className="p-8 text-center text-emerald-700">
                  <CheckCircle size={28} className="mx-auto mb-2"/> Tüm kontroller temiz.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

